import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getAdminParents, deleteUser, type AdminParent } from "../../services/users";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import LoginAsButton from "../../components/LoginAsButton";
import Pagination, { usePagination } from "../../components/Pagination";

export default function Parents() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [parents, setParents] = useState<AdminParent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  useEffect(() => {
    fetchParents();
  }, []);

  const fetchParents = async () => {
    try {
      setLoading(true);
      const data = await getAdminParents();
      setParents(data);
    } catch (err) {
      console.error("Failed to load parents", err);
      toast.error(t("parents.loadError", "Nu s-au putut încărca părinții"));
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return parents;
    return parents.filter(
      (p) =>
        (p.full_name || "").toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q) ||
        (p.phone || "").toLowerCase().includes(q) ||
        (p.city || "").toLowerCase().includes(q) ||
        (p.club?.name || "").toLowerCase().includes(q) ||
        p.children.some((c) => (c.full_name || "").toLowerCase().includes(q))
    );
  }, [parents, searchTerm]);

  // Lista vine întreagă de la server, deci paginăm în client.
  const { page, setPage, pageSize, total, visible: pagedParents } = usePagination(filtered);

  const handleDeleteClick = (id: string) => {
    if (deleteConfirmId === id) {
      handleDelete(id);
    } else {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId((cur) => (cur === id ? null : cur)), 3000);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteUser(id);
      setParents((prev) => prev.filter((p) => p.id !== id));
      setDeleteConfirmId(null);
      toast.success(t("parents.deleted", "Cont de părinte șters"));
    } catch (err) {
      console.error("Failed to delete parent", err);
      toast.error(t("parents.deleteError", "Nu s-a putut șterge contul de părinte"));
    }
  };

  const fmtDate = (s: string) => new Date(s).toLocaleDateString("ro-RO", { day: "numeric", month: "short", year: "numeric" });
  const cell = "px-6 py-4 text-sm";
  const th = "px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap";

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-[1500px]">
        <AdminPageHeader
          eyebrow="Utilizatori și Membri"
          title={t("parents.title", "Părinți")}
          subtitle={
            <>
              {t("parents.subtitle", "Gestionare conturi părinți")} (
              <span className="tabular-nums">{parents.length}</span>)
            </>
          }
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M16 3.13a4 4 0 010 7.75M8 3.13a4 4 0 000 7.75" />
            </svg>
          }
        />

        {/* Search */}
        <div className="mb-6 bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          <div className="relative">
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("parents.search", "Caută după nume, email, telefon, oraș, copil sau club…")}
              className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className={th}>{t("parents.col_parent", "Părinte")}</th>
                  <th className={th}>{t("parents.col_phone", "Telefon")}</th>
                  <th className={th}>{t("parents.col_city", "Oraș")}</th>
                  <th className={th}>{t("parents.col_children", "Copil")}</th>
                  <th className={th}>{t("parents.col_club", "Club")}</th>
                  <th className={th}>{t("parents.col_joined", "Înscris")}</th>
                  <th className={`${th} text-right`}>{t("common.actions", "Acțiuni")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-16 text-center text-slate-400">
                      <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-3" />
                      {t("common.loading", "Se încarcă...")}
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-16 text-center">
                      <div className="text-slate-300 text-4xl mb-3">👪</div>
                      <p className="text-slate-600 font-medium">{t("parents.empty", "Niciun cont de părinte")}</p>
                      <p className="text-slate-400 text-xs mt-1">
                        {t("parents.emptyHint", "Conturile de părinte se creează automat din datele de tutore ale sportivilor.")}
                      </p>
                    </td>
                  </tr>
                ) : (
                  pagedParents.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                      <td className={cell}>
                        <button
                          onClick={() => navigate(`/admin/users/${p.id}`)}
                          className="flex items-center gap-3 min-w-0 text-left group"
                        >
                          <div className="w-9 h-9 rounded-xl shrink-0 grid place-items-center text-white text-sm font-bold bg-gradient-to-br from-indigo-500 to-violet-600">
                            {(p.full_name || p.email).charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-800 truncate group-hover:text-indigo-600">{p.full_name || "—"}</div>
                            <div className="text-xs text-slate-400 truncate">{p.email}</div>
                          </div>
                        </button>
                      </td>
                      <td className={`${cell} text-slate-600 tabular-nums whitespace-nowrap`}>{p.phone || <span className="text-slate-300">—</span>}</td>
                      <td className={`${cell} text-slate-600`}>{p.city || <span className="text-slate-300">—</span>}</td>
                      <td className={cell}>
                        {p.children.length === 0 ? (
                          <span className="text-slate-300">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {p.children.map((c) => (
                              <button
                                key={c.id}
                                onClick={() => navigate(`/admin/users/${c.id}`)}
                                className="px-2 py-0.5 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100 hover:bg-emerald-100"
                                title={t("parents.viewChild", "Vezi sportivul")}
                              >
                                {c.full_name || t("parents.childUnnamed", "Sportiv")}
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className={`${cell} text-slate-600`}>{p.club?.name || <span className="text-slate-300">—</span>}</td>
                      <td className={`${cell} text-slate-500 whitespace-nowrap text-xs`}>{fmtDate(p.created_at)}</td>
                      <td className={`${cell} text-right`}>
                        <div className="inline-flex items-center gap-2 justify-end">
                          <LoginAsButton userId={p.id} />
                          {deleteConfirmId === p.id ? (
                            <button
                              onClick={() => handleDeleteClick(p.id)}
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-600 text-white hover:bg-rose-700"
                            >
                              {t("common.confirm", "Confirmă")}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDeleteClick(p.id)}
                              className="w-8 h-8 rounded-lg grid place-items-center text-slate-500 hover:bg-rose-50 hover:text-rose-600"
                              title={t("common.delete", "Șterge")}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M7 7l1 12a2 2 0 002 2h4a2 2 0 002-2l1-12M9 7V4h6v3" /></svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
        </div>
      </div>
    </div>
  );
}
